import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';

import { environment } from '../../../environments/environment';

type GtagCommand = 'config' | 'event' | 'js';
type GtagParams = Record<string, string | number | boolean | undefined>;
type GtagFn = (command: GtagCommand, target: string | Date, params?: GtagParams) => void;

export interface Ga4PageViewParams {
  contentGroup?: string;
  contentId?: string;
  contentType?: string;
  pageLocation?: string;
  pageTitle?: string;
  pageReferrer?: string;
}

@Injectable({
  providedIn: 'root',
})
export class Ga4AnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly measurementId = environment.gaMeasurementId;

  private isInitialized = false;
  private gtag?: GtagFn;

  public initialize(): void {
    if (this.isInitialized || !this.measurementId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.isInitialized = true;
    this.gtag = this.initializeGtag();
    this.gtag('js', new Date());
    this.gtag('config', this.measurementId, {
      send_page_view: false,
    });
  }

  public trackPageView(params: Ga4PageViewParams = {}): void {
    this.upsertOrRemoveMetaName('ga4:content_group', params.contentGroup);
    this.upsertOrRemoveMetaName('ga4:content_id', params.contentId);
    this.upsertOrRemoveMetaName('ga4:content_type', params.contentType);

    if (!this.measurementId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialize();
    this.gtag?.('event', 'page_view', {
      content_group: params.contentGroup,
      content_id: params.contentId,
      content_type: params.contentType,
      page_location: params.pageLocation ?? window.location.href,
      page_title: params.pageTitle ?? this.document.title,
      page_referrer: params.pageReferrer ?? this.document.referrer,
    });
  }

  public trackEvent(name: string, params: GtagParams = {}): void {
    if (!this.measurementId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialize();
    this.gtag?.('event', name, params);
  }

  private initializeGtag(): GtagFn {
    const analyticsWindow = window as Window & {
      dataLayer?: IArguments[];
      gtag?: GtagFn;
    };

    analyticsWindow.dataLayer ??= [];
    analyticsWindow.gtag ??= function gtag(
      command: GtagCommand,
      target: string | Date,
      params?: GtagParams,
    ): void {
      void command;
      void target;
      void params;
      // Google tag consumes the native `arguments` object from the bootstrap queue.
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    };

    if (!this.document.querySelector(`script[src*="${this.measurementId}"]`)) {
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      this.document.head.appendChild(script);
    }

    return analyticsWindow.gtag;
  }

  private upsertOrRemoveMetaName(name: string, content: string | undefined): void {
    if (!content) {
      this.meta.removeTag(`name="${name}"`);
      return;
    }

    this.meta.updateTag({
      name,
      content,
    });
  }
}
